import { debounce } from 'lodash';
import { useEffect, useMemo, useRef } from 'react';

type DebouncedFunction<T extends (...args: unknown[]) => unknown> = ((
  ...args: Parameters<T>
) => void) & { cancel: () => void };

export interface UseDebouncedCallbackOptions {
  /** Delay in milliseconds */
  delay: number;
  /** Execute on first call, ignore subsequent calls until delay passes. Use for button clicks. */
  leading?: boolean;
  /** Execute after delay passes since last call. Default true. */
  trailing?: boolean;
}

/**
 * Debounced callback that properly integrates with React lifecycles.
 * - Cancels pending execution on unmount (no stale state updates)
 * - Uses ref for callback to avoid stale closures when delay/options change
 * - Returns a stable function reference (stable per delay/leading/trailing)
 *
 * For button clicks to prevent double-clicks: use leading: true, trailing: false
 */
export function useDebouncedCallback<T extends (...args: unknown[]) => unknown>(
  callback: T,
  { delay, leading = false, trailing = true }: UseDebouncedCallbackOptions,
): DebouncedFunction<T> {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  const debouncedFn = useMemo(
    () =>
      debounce(
        (...args: Parameters<T>) => {
          callbackRef.current(...args);
        },
        delay,
        { leading, trailing },
      ) as DebouncedFunction<T>,
    [delay, leading, trailing],
  );

  useEffect(() => {
    return () => {
      debouncedFn.cancel();
    };
  }, [debouncedFn]);

  return debouncedFn;
}
